import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { Reveal, SectionLabel } from "@/components/motion";

const css = `
  @keyframes proc-float {
    0%   { transform: translateY(0px); }
    100% { transform: translateY(-8px); }
  }
  @keyframes proc-blink {
    0%,100% { opacity: 1; }
    50%     { opacity: 0.3; }
  }
  @keyframes proc-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes proc-bar {
    from { width: 0%; }
    to   { width: var(--w); }
  }
  @keyframes proc-dash {
    from { stroke-dashoffset: 120; }
    to   { stroke-dashoffset: 0; }
  }
  @keyframes proc-pulse-ring {
    0%   { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(2); opacity: 0; }
  }
`;

/* ─── Step Illustrations ─────────────────────────────────────── */

/* 01 Discover — user interview + goal list */
const DiscoverIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center gap-4">
    {/* Interview card */}
    <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-3.5 w-[120px] space-y-2"
      style={{ animation: "proc-float 3s ease-in-out infinite alternate" }}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#09B2ED] to-[#2563EB] flex items-center justify-center text-white text-[9px] font-bold">U</div>
        <div className="space-y-1">
          <div className="h-1.5 bg-slate-200 rounded w-14" />
          <div className="h-1 bg-slate-100 rounded w-10" />
        </div>
      </div>
      <div className="space-y-1.5 pt-1 border-t border-slate-50">
        {["What's your goal?", "Who are the users?", "Current blockers?"].map((q, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <div className="w-3 h-3 rounded-full border-2 border-[#09B2ED] shrink-0 mt-0.5" />
            <div className="text-[7px] text-slate-500 leading-tight">{q}</div>
          </div>
        ))}
      </div>
    </div>
    {/* Insight chips */}
    <div className="space-y-2">
      {[
        { label: "Goals mapped", color: "#09B2ED" },
        { label: "Users identified", color: "#2563EB" },
        { label: "Constraints set", color: "#91E0F1" },
      ].map((c, i) => (
        <div key={i} className="flex items-center gap-1.5 bg-white border border-slate-100 rounded-full px-2.5 py-1 shadow-sm"
          style={{ animation: `proc-float ${2.5 + i * 0.4}s ease-in-out ${i * 0.3}s infinite alternate` }}>
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} />
          <span className="text-[8px] font-semibold text-slate-600">{c.label}</span>
        </div>
      ))}
    </div>
  </div>
);

/* 02 Design — Figma-style artboard with cursors */
const DesignIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="bg-[#1E1E2E] rounded-2xl shadow-xl overflow-hidden w-[180px] border border-white/10"
      style={{ animation: "proc-float 3.5s ease-in-out infinite alternate" }}>
      {/* toolbar */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10">
        <div className="w-2 h-2 rounded-full bg-rose-500/70" />
        <div className="w-2 h-2 rounded-full bg-amber-400/70" />
        <div className="w-2 h-2 rounded-full bg-emerald-500/70" />
        <span className="ml-auto text-[7px] text-white/30 font-mono">homepage_v4</span>
      </div>
      {/* canvas */}
      <div className="p-3 space-y-2 relative">
        <div className="h-4 bg-[#2563EB] rounded-lg w-[70%]" />
        <div className="h-2.5 bg-white/10 rounded w-[85%]" />
        <div className="h-2 bg-white/10 rounded w-[55%]" />
        <div className="flex gap-2 mt-2">
          <div className="h-5 w-14 bg-[#09B2ED] rounded-lg" />
          <div className="h-5 w-14 bg-white/5 rounded-lg border border-white/15" />
        </div>
        {/* cursor A */}
        <div className="absolute" style={{ top: "18px", left: "50px", animation: "none" }}>
          <svg width="10" height="13" viewBox="0 0 10 13"><path d="M0 0l3.5 12 2-3.5 3.5 1.5z" fill="#34d399"/></svg>
          <span className="absolute left-3 -top-0.5 bg-emerald-500 text-[6px] px-1 rounded text-white font-mono">Alice</span>
        </div>
        {/* cursor B */}
        <div className="absolute" style={{ top: "40px", left: "90px" }}>
          <svg width="10" height="13" viewBox="0 0 10 13"><path d="M0 0l3.5 12 2-3.5 3.5 1.5z" fill="#fbbf24"/></svg>
          <span className="absolute left-3 -top-0.5 bg-amber-500 text-[6px] px-1 rounded text-white font-mono">Bob</span>
        </div>
      </div>
      <div className="flex justify-between px-3 pb-2 text-[6px] text-white/25 font-mono">
        <span>1440×900</span><span>Components: 8</span>
      </div>
    </div>
  </div>
);

/* 03 Build — code editor with CI pipeline */
const BuildIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center gap-3">
    {/* Code window */}
    <div className="bg-slate-900 rounded-2xl shadow-xl overflow-hidden w-[130px] border border-slate-700 font-mono text-[7.5px]"
      style={{ animation: "proc-float 3s ease-in-out infinite alternate" }}>
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-800 bg-slate-950">
        <div className="w-2 h-2 rounded-full bg-rose-500/70" />
        <div className="w-2 h-2 rounded-full bg-amber-400/70" />
        <div className="w-2 h-2 rounded-full bg-emerald-500/70" />
      </div>
      <div className="p-2.5 space-y-1">
        <div><span className="text-purple-400">const</span> <span className="text-blue-300">api</span> <span className="text-white">=</span> <span className="text-emerald-400">new</span> <span className="text-yellow-300">Client</span>()</div>
        <div className="pl-2"><span className="text-blue-300">api</span><span className="text-white">.</span><span className="text-yellow-300">get</span>(<span className="text-emerald-300">"/users"</span>)</div>
        <div className="pl-4 text-slate-400">.then(<span className="text-orange-300">res</span> =&gt;</div>
        <div className="pl-6 text-slate-300">setData(<span className="text-orange-300">res</span>))</div>
        <div className="text-[#09B2ED] mt-1">// Sprint 3 — Day 4</div>
      </div>
    </div>
    {/* CI pipeline */}
    <div className="space-y-1.5">
      {[
        { label: "Tests", status: "pass", color: "#34d399" },
        { label: "Build", status: "pass", color: "#34d399" },
        { label: "Deploy", status: "live", color: "#09B2ED" },
      ].map((s, i) => (
        <div key={i} className="flex items-center gap-2 bg-white rounded-xl border border-slate-100 px-2.5 py-1.5 shadow-sm w-[85px]">
          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color, animation: `proc-blink 1.8s ${i * 0.4}s ease-in-out infinite` }} />
          <span className="text-[8px] font-semibold text-slate-600">{s.label}</span>
          <span className="text-[7px] font-mono ml-auto" style={{ color: s.color }}>{s.status}</span>
        </div>
      ))}
    </div>
  </div>
);

/* 04 Launch — rocket + checklist */
const LaunchIllustration = () => (
  <div className="relative w-full h-full flex items-center justify-center gap-3">
    {/* Launch card */}
    <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-3.5 w-[120px]"
      style={{ animation: "proc-float 2.8s ease-in-out infinite alternate" }}>
      <div className="text-2xl text-center mb-2">🚀</div>
      <div className="text-[8px] font-bold text-center text-[#01193D] mb-2.5">Go-Live Checklist</div>
      {["QA passed", "Staging signed off", "DNS live", "Monitoring on"].map((item, i) => (
        <div key={i} className="flex items-center gap-1.5 py-0.5">
          <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
            <svg width="7" height="5" viewBox="0 0 7 5"><path d="M1 2.5l1.5 1.5 3.5-3" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round"/></svg>
          </div>
          <span className="text-[7.5px] text-slate-600">{item}</span>
        </div>
      ))}
    </div>
    {/* Pulse rings */}
    <div className="relative w-10 h-10 flex items-center justify-center">
      <div className="absolute w-10 h-10 rounded-full bg-emerald-400/30 origin-center"
        style={{ animation: "proc-pulse-ring 2s ease-out infinite" }} />
      <div className="absolute w-10 h-10 rounded-full bg-emerald-400/20 origin-center"
        style={{ animation: "proc-pulse-ring 2s 0.7s ease-out infinite" }} />
      <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
        <svg width="10" height="8" viewBox="0 0 10 8"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
      </div>
    </div>
  </div>
);

/* 05 Grow — analytics trend chart */
const GrowIllustration = () => {
  const points = [30, 42, 38, 55, 50, 68, 65, 80, 76, 92];
  const toPath = (pts) => pts.map((y, x) => `${x === 0 ? "M" : "L"} ${x * 18} ${90 - y}`).join(" ");
  return (
    <div className="relative w-full h-full flex items-center justify-center gap-3">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-3.5 w-[160px]"
        style={{ animation: "proc-float 3.2s ease-in-out infinite alternate" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[8.5px] font-bold text-[#01193D]">Growth Metrics</span>
          <span className="text-[7px] text-emerald-500 font-mono font-bold">+34%</span>
        </div>
        {/* SVG line chart */}
        <div className="bg-[#F0F9FF] rounded-xl p-2 border border-slate-100 overflow-hidden">
          <svg viewBox="0 0 162 96" className="w-full h-12" preserveAspectRatio="none">
            <defs>
              <linearGradient id="pgrow" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#09B2ED" stopOpacity="0.3"/>
                <stop offset="100%" stopColor="#09B2ED" stopOpacity="0"/>
              </linearGradient>
            </defs>
            <path d={`${toPath(points)} L 162 90 L 0 90 Z`} fill="url(#pgrow)" />
            <path d={toPath(points)} stroke="#09B2ED" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"
              style={{ strokeDasharray: 300, animation: "proc-dash 1.5s ease-out forwards" }} />
            {/* Last dot */}
            <circle cx="162" cy={`${90 - points[points.length-1]}`} r="3.5" fill="#09B2ED" />
          </svg>
          <div className="flex justify-between text-[6.5px] text-slate-400 font-mono mt-1">
            <span>W1</span><span>W4</span><span>W8</span><span>W12</span>
          </div>
        </div>
        {/* Metric pills */}
        <div className="flex gap-1.5 mt-2.5">
          {[
            { label: "CAC", val: "−18%", color: "#34d399" },
            { label: "LTV", val: "+41%", color: "#09B2ED" },
            { label: "NPS", val: "72", color: "#2563EB" },
          ].map(m => (
            <div key={m.label} className="flex-1 bg-[#F8FAFC] rounded-lg p-1 text-center border border-slate-100">
              <div className="text-[7px] text-slate-400">{m.label}</div>
              <div className="text-[8px] font-bold" style={{ color: m.color }}>{m.val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const STEPS = [
  {
    num: "01",
    title: "Discover",
    desc: "We dig into your goals, users, and constraints — mapping everything before writing a single line of code.",
    color: "#09B2ED",
    bg: "from-[#E0F7FF] to-[#F0FBFF]",
    border: "border-[#CAF2F9]",
    Illustration: DiscoverIllustration,
  },
  {
    num: "02",
    title: "Design",
    desc: "Interactive prototypes and design systems that make the solution tangible — fast. Real feedback, not wireframe guesses.",
    color: "#2563EB",
    bg: "from-[#EFF6FF] to-[#F5F9FF]",
    border: "border-[#BFDBFE]",
    Illustration: DesignIllustration,
  },
  {
    num: "03",
    title: "Build",
    desc: "Agile sprints with weekly demos. You see the product grow in real time — no black boxes, no surprises.",
    color: "#09B2ED",
    bg: "from-[#F8FAFC] to-[#F0F9FF]",
    border: "border-slate-200",
    Illustration: BuildIllustration,
  },
  {
    num: "04",
    title: "Launch",
    desc: "Rigorous QA, staged rollouts, and drama-free go-lives. We own the launch as much as the build.",
    color: "#2563EB",
    bg: "from-[#ECFDF5] to-[#F0FFF8]",
    border: "border-[#A7F3D0]",
    Illustration: LaunchIllustration,
  },
  {
    num: "05",
    title: "Grow",
    desc: "Post-launch analytics, iteration loops, and growth experiments. The engagement doesn't end at delivery.",
    color: "#09B2ED",
    bg: "from-[#E0F7FF] to-[#F0FBFF]",
    border: "border-[#CAF2F9]",
    Illustration: GrowIllustration,
  },
];

export default function ProcessTimeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 55%"] });

  return (
    <section className="px-5 sm:px-8 py-20 lg:py-28 bg-white" data-testid="process-timeline-section">
      <style>{css}</style>
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl mb-16">
          <SectionLabel>Our Process</SectionLabel>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D] leading-tight">
            A method that keeps you<br className="hidden sm:block" /> in the loop
          </h2>
          <p className="text-[#475569] text-base lg:text-lg mt-4">
            Five clear stages. No agency mystery. You're involved at every checkpoint.
          </p>
        </Reveal>

        {/* Vertical timeline */}
        <div ref={ref} className="relative">
          {/* Vertical progress line — desktop only */}
          <div className="hidden lg:block absolute left-[28px] top-6 bottom-6 w-[2px] bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              style={{ scaleY: scrollYProgress }}
              className="w-full h-full origin-top rounded-full"
              style={{ background: "linear-gradient(to bottom, #2563EB, #09B2ED)", scaleY: scrollYProgress, transformOrigin: "top" }}
              data-testid="process-line"
            />
          </div>

          <div className="space-y-8 lg:space-y-6">
            {STEPS.map((step, i) => {
              const even = i % 2 === 0;
              return (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, x: even ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.05, ease: [0.21, 0.6, 0.35, 1] }}
                  className="relative lg:pl-20"
                  data-testid={`process-step-${i}`}
                >
                  {/* Node dot */}
                  <div
                    className="hidden lg:flex absolute left-0 top-6 w-14 h-14 rounded-2xl items-center justify-center font-display font-bold text-sm text-white shadow-lg z-10"
                    style={{ background: `linear-gradient(135deg, ${step.color}, #2563EB)` }}
                  >
                    {step.num}
                  </div>

                  {/* Card */}
                  <div className={`group relative rounded-3xl border ${step.border} bg-gradient-to-br ${step.bg} overflow-hidden grid lg:grid-cols-2 gap-0 hover:shadow-xl transition-shadow duration-300`}>

                    {/* Left — text */}
                    <div className="p-7 lg:p-10 flex flex-col justify-center">
                      {/* Mobile step badge */}
                      <div
                        className="lg:hidden inline-flex w-10 h-10 rounded-xl items-center justify-center font-display font-bold text-sm text-white mb-5 self-start shadow-md"
                        style={{ background: `linear-gradient(135deg, ${step.color}, #2563EB)` }}
                      >
                        {step.num}
                      </div>

                      {/* Step number watermark */}
                      <div className="text-[80px] font-display font-black leading-none select-none mb-2 -mt-2"
                        style={{ color: `${step.color}10` }}>
                        {step.num}
                      </div>

                      <h3 className="font-display font-bold text-2xl lg:text-3xl text-[#01193D] -mt-8">{step.title}</h3>
                      <p className="text-[#475569] text-base leading-relaxed mt-3 max-w-sm">{step.desc}</p>

                      {/* Accent underline */}
                      <div className="mt-6 h-[3px] w-12 rounded-full" style={{ background: step.color }} />
                    </div>

                    {/* Right — illustration */}
                    <div className="relative h-52 lg:h-auto overflow-hidden rounded-r-3xl"
                      style={{ background: `${step.color}08` }}>
                      {/* subtle grid */}
                      <div className="absolute inset-0 opacity-30"
                        style={{
                          backgroundImage: `radial-gradient(circle, ${step.color}30 1px, transparent 1px)`,
                          backgroundSize: "20px 20px",
                        }}
                      />
                      <div className="relative h-full">
                        <step.Illustration />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
